import STATE from '../enums/state.js';

const selectors = {
  headerSpacer: '[data-header-spacer]',
  header: '[data-header]',
  logo: '[data-header-logo]',
  mobileMenu: '[data-drawer="mobile-menu"]',
};

class SiteHeader extends HTMLElement {
  constructor() {
    super();

    this.$header = document.querySelector(selectors.header);
    this.$headerSpacer = document.querySelector(selectors.headerSpacer);
    this.$mobileMenu = document.querySelector(selectors.mobileMenu);

    if (!this.$header) {
      return;
    }

    this.$logo = this.$header.querySelector(selectors.logo);
    this.$menus = [...document.querySelectorAll('[data-menu]')];
    this.$openMenus = [];
    this.$triggers = [...document.querySelectorAll('[data-menu-trigger]')];

    this.breakpoint = window.matchMedia('(min-width:1200px)');
    this.state = STATE.MEGAMENU.CLOSED;

    this.init();
    this.registerEventListeners();
  }

  init() {
    this.resizeLogo();
    this.headerSpacing();
  }

  registerEventListeners() {
    window.addEventListener(
      'scroll',
      () => {
        this.resizeLogo();
        this.closeOpenMenuImmediately();

        // Allow time for the header to resize before updating the spacing.
        setTimeout(() => {
          this.headerSpacing();
        }, 300);
      },
      {
        passive: true,
      }
    );

    window.addEventListener('resize', () => {
      this.resizeLogo();

      // Allow time for the header to resize before updating the spacing.
      setTimeout(() => {
        this.headerSpacing();
      }, 300);
    });

    this.$menus.forEach($menu => {
      $menu.addEventListener('mouseover', () => {
        this.keepMenuOpen($menu.dataset.menu);
      });

      $menu.addEventListener('mouseleave', () => {
        this.closeMenu($menu.dataset.menu, true);
      });
    });

    this.$triggers.forEach($trigger => {
      $trigger.addEventListener('mouseenter', () => {
        this.openMenu($trigger.dataset.menuTrigger);
      });

      $trigger.addEventListener('focus', event => {
        event.preventDefault();
        this.openMenu($trigger.dataset.menuTrigger);
      });

      $trigger.addEventListener('mouseleave', () => {
        this.closeMenu($trigger.dataset.menuTrigger, true);
      });
    });
  }

  headerSpacing() {
    const headerHeight = this.$header.offsetHeight;

    this.$headerSpacer.style.paddingTop = `${headerHeight}px`;
    this.$mobileMenu.style.top = `${headerHeight}px`;
  }

  resizeLogo() {
    // Check if the user is scrolled to the top of the page...
    if (window.pageYOffset <= 0) {
      // If so, grow the header.
      this.growLogo();
    } else {
      // Otherwise, shrink the header.
      this.shrinkLogo();
    }
  }

  growLogo() {
    // Check if the device is desktop...
    if (this.breakpoint.matches) {
      // If so, grow the logo.
      this.$logo.style.height = '80px';
      this.$logo.style.marginBottom = '10px';
      this.$logo.style.marginTop = '10px';
    } else {
      // Otherwise use the defaults.
      this.$logo.style.removeProperty('height');
      this.$logo.style.removeProperty('margin-bottom');
      this.$logo.style.removeProperty('margin-top');
    }
  }

  shrinkLogo() {
    // Check if the device is desktop...
    if (this.breakpoint.matches) {
      // If so, grow the logo.
      this.$logo.style.height = '46px';
      this.$logo.style.marginBottom = '8px';
      this.$logo.style.marginTop = '8px';
    } else {
      // Otherwise use the defaults.
      this.$logo.style.removeProperty('height');
      this.$logo.style.removeProperty('margin-bottom');
      this.$logo.style.removeProperty('margin-top');
    }
  }

  openMenu(menu) {
    let transition = true;

    // Check if the menu requesting to be opened is already open...
    if (this.openedMenu === menu) {
      // If so, do nothing.
      return;
    }

    // Check if a different menu is already open...
    if (this.openedMenu && this.openedMenu !== menu) {
      // If so, close the opened menu immediately.
      this.closeOpenMenuImmediately();
      transition = false;
    }

    const $menu = this.$header.querySelector(`[data-menu="${menu}"]`);
    const $trigger = this.$header.querySelector(
      `[data-menu-trigger="${menu}"]`
    );
    $menu.dataset.transition = transition;
    this.shouldCloseMenu = false;

    this.$openMenus = this.$menus.filter(
      $m => $m.getAttribute('aria-expanded') === 'true'
    );

    this.openedMenu = menu;
    requestAnimationFrame(() => {
      $menu.setAttribute('aria-expanded', true);
      $trigger.dataset.state = STATE.MEGAMENU.OPEN;
    });
  }

  closeMenu(menu, transition) {
    const $menu = this.$header.querySelector(`[data-menu="${menu}"]`);
    const $trigger = this.$header.querySelector(
      `[data-menu-trigger="${menu}"]`
    );
    $menu.dataset.transition = transition;
    this.shouldCloseMenu = true;

    this.$openMenus = this.$menus.filter(
      $m => $m.getAttribute('aria-expanded') === 'true'
    );

    this.closeMenuTimeout = setTimeout(() => {
      if (!this.shouldCloseMenu) {
        return;
      }

      this.openedMenu = undefined;
      requestAnimationFrame(() => {
        $menu.setAttribute('aria-expanded', false);
        $trigger.dataset.state = STATE.MEGAMENU.CLOSED;
      });
    }, 250);
  }

  closeOpenMenuImmediately() {
    if (!this.openedMenu) {
      return;
    }
    const $menu = this.$header.querySelector(
      `[data-menu="${this.openedMenu}"]`
    );
    const $trigger = this.$header.querySelector(
      `[data-menu-trigger="${this.openedMenu}"]`
    );
    $menu.dataset.transition = false;

    if (this.closeMenuTimeout) {
      clearTimeout(this.closeMenuTimeout);
      this.closeMenuTimeout = undefined;
    }

    this.openedMenu = undefined;
    requestAnimationFrame(() => {
      $menu.setAttribute('aria-expanded', false);
      $trigger.dataset.state = STATE.MEGAMENU.CLOSED;
    });
  }

  keepMenuOpen() {
    if (this.closeMenuTimeout) {
      clearTimeout(this.closeMenuTimeout);
      this.closeMenuTimeout = undefined;
    }
  }
}

customElements.define('site-header', SiteHeader);
